import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Gift, Copy, Check } from "lucide-react";
import { api, getSharedPublic } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SEEN_KEY = "levami_welcome_seen";

/** Tasteful first-visit welcome offer: newsletter signup in exchange for a discount code.
 * Shows once per visitor (localStorage), after a configurable delay.
 * Fully admin-configurable via the `welcome_popup` content block.
 */
export function WelcomePopup() {
  const { t } = useLocale();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("offer"); // offer -> code
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Never interrupt someone who is buying or tracking an order.
  const isQuietPage = /^\/(panier|commande|suivi-commande)/.test(location.pathname);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    let active = true;
    let timer;
    getSharedPublic("/content/welcome_popup")
      .then((res) => {
        if (!active) return;
        const data = res.data?.data || {};
        if (data.enabled === false || !data.coupon_code) return;
        setSettings(data);
        timer = setTimeout(() => {
          if (active && !localStorage.getItem(SEEN_KEY) && !/^\/(panier|commande|suivi-commande)/.test(window.location.pathname)) {
            setOpen(true);
          }
        }, Math.max(2, data.delay_seconds || 6) * 1000);
      })
      .catch(() => {});
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, []);

  // If the visitor navigates to cart/checkout while the popup is open, close it quietly
  // (without marking it as seen so it can show later).
  useEffect(() => {
    if (open && isQuietPage) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const dismiss = (v) => {
    if (!v) {
      localStorage.setItem(SEEN_KEY, "1");
      setOpen(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/newsletter/subscribe", { email, consent: true, source: "welcome_popup" });
      setStep("code");
      localStorage.setItem(SEEN_KEY, "1");
    } catch (err) {
      setError(err?.response?.data?.detail || t("welcome.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(settings.coupon_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!settings) return null;

  return (
    <Dialog open={open} onOpenChange={dismiss}>
      <DialogContent className="bg-levami-creme border-levami-sable max-w-sm p-0 overflow-hidden" data-testid="welcome-popup">
        <div className="bg-levami-ivoire border-b border-levami-sable px-8 pt-8 pb-6 text-center">
          <Gift className="h-7 w-7 text-levami-primary mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-serif-heading text-[22px] leading-snug text-levami-charbon">
            {step === "offer"
              ? t("welcome.title", { discount: settings.discount_label || "-10%" })
              : t("welcome.thanks_title")}
          </p>
        </div>
        <div className="px-8 pb-8 pt-5">
          {step === "offer" ? (
            <>
              <p className="text-sm text-levami-brun/75 leading-relaxed text-center mb-5">
                {t("welcome.text", { discount: settings.discount_label || "-10%" })}
              </p>
              <form onSubmit={subscribe} className="space-y-3">
                <Input
                  type="email"
                  required
                  placeholder={t("welcome.email_placeholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="welcome-popup-email-input"
                  className="bg-levami-creme h-11 text-center"
                />
                <Button type="submit" disabled={submitting} data-testid="welcome-popup-submit-button" className="w-full h-11 bg-levami-charbon text-levami-creme hover:bg-levami-brun rounded-full">
                  {submitting ? "..." : t("welcome.cta", { discount: settings.discount_label || "-10%" })}
                </Button>
                {error && <p className="text-xs text-levami-primary text-center" data-testid="welcome-popup-error">{error}</p>}
              </form>
              <button
                type="button"
                onClick={() => dismiss(false)}
                data-testid="welcome-popup-dismiss"
                className="block mx-auto mt-4 text-[11px] underline underline-offset-2 text-levami-brun/50 hover:text-levami-brun"
              >
                {t("welcome.no_thanks")}
              </button>
              <p className="text-[10px] text-levami-brun/40 text-center mt-3">{t("welcome.consent_note")}</p>
            </>
          ) : (
            <>
              <p className="text-sm text-levami-brun/75 leading-relaxed text-center mb-5">{t("welcome.code_text")}</p>
              <button
                type="button"
                onClick={copyCode}
                data-testid="welcome-popup-code"
                className="w-full flex items-center justify-center gap-2 border border-dashed border-levami-charbon rounded-lg py-3 mb-4 hover:bg-levami-ivoire transition-colors"
              >
                <span className="font-serif-heading text-xl tracking-[0.2em] text-levami-charbon">{settings.coupon_code}</span>
                {copied ? <Check className="h-4 w-4 text-levami-olive" /> : <Copy className="h-4 w-4 text-levami-brun/60" />}
              </button>
              <Button onClick={() => dismiss(false)} data-testid="welcome-popup-close-button" className="w-full h-11 bg-levami-charbon text-levami-creme hover:bg-levami-brun rounded-full">
                {t("welcome.start_shopping")}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
